import { useRef, useState, type ReactNode } from 'react'
import { AlertTriangle, Check, ChevronLeft, Clock, Info, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { toast } from '@/hooks/use-toast'
import {
  useCheckHold,
  useCreateOwnerBlock,
  useReleaseOwnerBlock,
  type usePropertyBlocks,
} from '@/hooks/use-property-blocks'
import type { ExternalBlockRecord, OwnerBlockRecord } from '@/api/client'
import { cn } from '@/lib/utils'
import {
  BLOCK_CHECKIN_TIME_LABEL,
  BLOCK_CHECKOUT_TIME_LABEL,
  MAX_CC_EMAILS,
  OVERLAP_LIST_LIMIT,
  blockErrorCode,
  blockingWindowCopy,
  cleaningRequestCopy,
  describeBlockError,
  longDate,
  nextDay,
  nightsBetween,
  parseCcEmails,
  rangeTitle,
  shortDate,
  startHeading,
  statusOf,
} from './block-utils'

type BlocksQuery = ReturnType<typeof usePropertyBlocks>

const SHEET_FOOTER = 'flex shrink-0 flex-wrap justify-end gap-2.5 border-t border-border px-6 py-4'

function Pill({
  variant,
  children,
}: {
  variant: 'mint' | 'danger' | 'amber' | 'slate'
  children: ReactNode
}) {
  const classes = {
    mint: 'bg-mintHover text-mint',
    danger: 'bg-danger/20 text-danger',
    amber: 'bg-amber/[0.14] text-amber',
    slate: 'bg-slate-500/20 text-slate-300',
  }[variant]
  return (
    <span
      className={cn(
        'inline-flex w-fit items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold',
        classes
      )}
    >
      {children}
    </span>
  )
}

function FieldLabel({ htmlFor, children }: { htmlFor?: string; children: ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-1.5 block text-[11.5px] font-semibold uppercase tracking-wide text-textFaint"
    >
      {children}
    </label>
  )
}

function DetailField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-textFaint">
        {label}
      </div>
      <div className="break-words text-sm text-text">{children}</div>
    </div>
  )
}

function InlineAlert({
  variant = 'amber',
  icon = 'warn',
  testId,
  children,
}: {
  variant?: 'amber' | 'danger' | 'info'
  icon?: 'warn' | 'info'
  testId?: string
  children: ReactNode
}) {
  const styles = {
    danger: 'border-danger/30 bg-danger/10 text-danger',
    amber: 'border-amber/30 bg-amber/10 text-amber',
    info: 'border-borderStrong bg-surface2 text-textMuted',
  }[variant]
  const Icon = icon === 'info' ? Info : AlertTriangle
  return (
    <div
      role={variant === 'danger' ? 'alert' : 'status'}
      data-testid={testId}
      className={cn(
        'flex items-start gap-2 rounded-input border px-3 py-2.5 text-[11.5px] leading-relaxed',
        styles
      )}
    >
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <div className="min-w-0 break-words">{children}</div>
    </div>
  )
}

function CheckboxRow({
  checked,
  onChange,
  disabled,
  children,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <label
      className={cn(
        'flex items-start gap-2.5 text-xs text-textMuted',
        disabled ? 'opacity-50' : 'cursor-pointer'
      )}
    >
      <input
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={event => onChange(event.target.checked)}
      />
      <span
        className={cn(
          'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-mint peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-bg',
          checked ? 'border-mint bg-mint' : 'border-borderStrong bg-surface2'
        )}
        aria-hidden
      >
        {checked ? <Check className="h-2.5 w-2.5 text-charcoal" /> : null}
      </span>
      <span className="min-w-0">{children}</span>
    </label>
  )
}

/** Where a block's sheet goes back to: the day list it was opened from. */
export interface BlockSheetBack {
  label: string
  onBack: () => void
}

function SheetFrame({
  testId,
  title,
  description,
  pill,
  back,
  onClose,
  locked = false,
  children,
  footer,
}: {
  testId: string
  title: string
  description: string
  pill?: ReactNode
  /** The way back to the list this sheet was opened from, above the title. */
  back?: BlockSheetBack
  onClose: () => void
  /** A write is out: the sheet stays until it answers, so its result is never lost with it. */
  locked?: boolean
  children: ReactNode
  footer: ReactNode
}) {
  return (
    <Sheet open onOpenChange={open => !open && !locked && onClose()}>
      <SheetContent aria-describedby={`${testId}-description`} data-testid={testId}>
        <SheetHeader>
          {back && (
            <button
              type="button"
              onClick={back.onBack}
              disabled={locked}
              data-testid="block-sheet-back"
              className="-ml-1 inline-flex min-h-11 w-fit items-center gap-1 rounded px-1 text-xs font-medium text-mint hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint disabled:opacity-50"
            >
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
              {back.label}
            </button>
          )}
          <SheetTitle className="break-words">{title}</SheetTitle>
          <SheetDescription id={`${testId}-description`}>{description}</SheetDescription>
          {pill}
        </SheetHeader>
        <SheetBody className="space-y-4 py-[18px]">{children}</SheetBody>
        <div className={SHEET_FOOTER}>{footer}</div>
      </SheetContent>
    </Sheet>
  )
}

// -- create -----------------------------------------------------------------

export interface BlockCreateSheetProps {
  propertyId: string
  /** First blocked night the form opens with, `YYYY-MM-DD`; it can be changed in the form. */
  initialStart: string
  /** Exclusive checkout day the form opens with, for a range dragged on the grid; one night when left out. */
  initialEnd?: string
  /** What the drafted range runs over, as lines of words: informational only. */
  overlapsFor: (start: string, end: string) => string[]
  /** The property's own today, so the picker can mark it. */
  today?: string | null
  onClose: () => void
}

interface Draft {
  start: string
  end: string
  note: string
  cleaning_needed: boolean
  cc_emails: string[]
  notify_owner: boolean
}

function sameDraft(a: Draft | null, b: Draft): boolean {
  return (
    !!a &&
    a.start === b.start &&
    a.end === b.end &&
    a.note === b.note &&
    a.cleaning_needed === b.cleaning_needed &&
    a.notify_owner === b.notify_owner &&
    a.cc_emails.join(',') === b.cc_emails.join(',')
  )
}

/**
 * "Block dates": the portal's block panel on the admin Calendar tab. Any range
 * may be submitted - Uplisting stacks overlapping blocks and bookings and never
 * refuses one - so an overlap is a note, not a stop.
 *
 * The block id is made here and reused when the same draft is sent again after
 * a lost response, so the server treats the repeat as the same request; any
 * edit gets a fresh id (the server would reject a different body under an old
 * id). Nothing is drawn before the server answers.
 */
export function BlockCreateSheet({
  propertyId,
  initialStart,
  initialEnd,
  overlapsFor,
  today,
  onClose,
}: BlockCreateSheetProps) {
  // One night to start with, so the picker opens on the start's month, not today's.
  const [start, setStart] = useState(initialStart)
  const [end, setEnd] = useState(() =>
    initialEnd && nightsBetween(initialStart, initialEnd) !== null
      ? initialEnd
      : nextDay(initialStart)
  )
  const [note, setNote] = useState('')
  const [cleaning, setCleaning] = useState(false)
  const [silent, setSilent] = useState(false)
  const [ccText, setCcText] = useState('')
  const [blockId, setBlockId] = useState(() => crypto.randomUUID())
  const lastSent = useRef<Draft | null>(null)
  const sending = useRef(false)
  const create = useCreateOwnerBlock(propertyId)

  // Moving the start past the end moves the end along with it, to one night.
  const changeStart = (next: string) => {
    setStart(next)
    if (nightsBetween(next, end) === null) setEnd(nextDay(next))
  }

  const nights = end ? nightsBetween(start, end) : null
  const endInvalid = nights === null
  const { emails, invalid } = parseCcEmails(ccText)
  const tooManyCc = emails.length > MAX_CC_EMAILS
  const ccOk = silent || (invalid.length === 0 && !tooManyCc)
  const overlaps = nights ? overlapsFor(start, end) : []
  const canSubmit = nights !== null && ccOk && !create.isPending

  const submit = () => {
    if (!canSubmit || sending.current) return
    const draft: Draft = {
      start,
      end,
      note: note.trim(),
      cleaning_needed: cleaning,
      // Nothing to cc on a block that sends the owner no email.
      cc_emails: silent ? [] : emails,
      notify_owner: !silent,
    }
    // The same draft is a retry (same id); an edited one is a new request.
    const id = sameDraft(lastSent.current, draft) ? blockId : crypto.randomUUID()
    if (id !== blockId) setBlockId(id)
    lastSent.current = draft
    sending.current = true
    create.mutate(
      {
        block_id: id,
        start: draft.start,
        end: draft.end,
        note: draft.note,
        cleaning_needed: draft.cleaning_needed,
        ...(draft.cc_emails.length > 0 ? { cc_emails: draft.cc_emails } : {}),
        // Left out when on: the server's default is to email the owner.
        ...(silent ? { notify_owner: false } : {}),
      },
      {
        onSuccess: () => {
          toast({
            title: 'Dates blocked',
            description: 'Syncing to Airbnb, VRBO and the other channels.',
          })
          onClose()
        },
        onError: error => {
          // A clash means this id already stands for a different body: start over with a new one.
          if (blockErrorCode(error) === 'block_id_conflict') {
            lastSent.current = null
            setBlockId(crypto.randomUUID())
          }
        },
        onSettled: () => {
          sending.current = false
        },
      }
    )
  }

  const busy = create.isPending
  return (
    <SheetFrame
      testId="block-create-sheet"
      title="Block dates"
      description={startHeading(start)}
      pill={<Pill variant="mint">Available</Pill>}
      onClose={onClose}
      locked={busy}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={!canSubmit}>
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            {busy ? 'Blocking…' : 'Block these dates'}
          </Button>
        </>
      }
    >
      <div aria-busy={busy} className={cn('space-y-[18px]', busy && 'pointer-events-none')}>
        <div>
          <FieldLabel>Date range</FieldLabel>
          <div className="grid grid-cols-2 gap-2.5">
            <div className="min-w-0">
              <span id="block-start-label" className="sr-only">
                Start date
              </span>
              <DatePicker
                id="block-start"
                labelledBy="block-start-label"
                value={start}
                onChange={changeStart}
                today={today}
                disabled={busy}
              />
            </div>
            <div className="min-w-0">
              <span id="block-end-label" className="sr-only">
                End date
              </span>
              <DatePicker
                id="block-end"
                labelledBy="block-end-label"
                value={end}
                onChange={setEnd}
                today={today}
                disabled={busy}
              />
            </div>
          </div>
          {!end ? (
            <p className="mt-1.5 text-[11.5px] text-textMuted">Pick an end date.</p>
          ) : endInvalid ? (
            <p className="mt-1.5 text-[11px] text-danger" role="alert">
              The end date must be after the start date.
            </p>
          ) : (
            <>
              <p className="mt-2 text-[11.5px] text-textMuted">
                Blocking <strong className="tabular-nums text-text">{nights}</strong> night
                {nights === 1 ? '' : 's'}. The end date stays open for a new check-in.
              </p>
              <div className="mt-2.5 flex items-start gap-2 rounded-input border border-border bg-surface2 px-2.5 py-2.5 text-[11.5px] leading-relaxed text-textMuted">
                <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-textFaint" aria-hidden />
                <span>{blockingWindowCopy(shortDate(start), shortDate(end))}</span>
              </div>
            </>
          )}
          {overlaps.length > 0 ? (
            <div className="mt-2.5">
              <InlineAlert variant="info" icon="info" testId="block-overlap-note">
                <div className="font-semibold text-text">These dates overlap existing items.</div>
                <ul className="mt-1 list-disc pl-4">
                  {overlaps.slice(0, OVERLAP_LIST_LIMIT).map(line => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                {overlaps.length > OVERLAP_LIST_LIMIT ? (
                  <div className="mt-1">and {overlaps.length - OVERLAP_LIST_LIMIT} more.</div>
                ) : null}
                <div className="mt-1">
                  Uplisting accepts overlaps and keeps every block and booking. Blocking does not
                  change a reservation.
                </div>
              </InlineAlert>
            </div>
          ) : null}
        </div>

        <div>
          <FieldLabel htmlFor="block-reason">Reason (optional, seen only by our team)</FieldLabel>
          <textarea
            id="block-reason"
            rows={2}
            value={note}
            disabled={busy}
            onChange={event => setNote(event.target.value)}
            placeholder="e.g. Extra cleaning before the next arrival"
            className="w-full resize-none rounded-input border border-border bg-surface2 px-2.5 py-2 text-sm text-text placeholder:text-textFaint focus-visible:border-mint focus-visible:outline-hidden"
          />
        </div>

        <div>
          <FieldLabel>Cleaning</FieldLabel>
          <CheckboxRow checked={cleaning} onChange={setCleaning} disabled={busy}>
            {cleaningRequestCopy(end && nights ? longDate(end) : null)}
          </CheckboxRow>
        </div>

        <div>
          <CheckboxRow checked={silent} onChange={setSilent} disabled={busy}>
            <span className="font-semibold text-text">Do not email the owner</span>
            <span className="mt-0.5 block text-[11.5px] font-normal text-textMuted">
              The owner gets no confirmation or reminder email. Cleaning is still scheduled.
            </span>
          </CheckboxRow>
        </div>

        {!silent ? (
          <div>
            <FieldLabel htmlFor="block-cc">Cc extra emails on reminders (optional)</FieldLabel>
            <input
              id="block-cc"
              type="text"
              value={ccText}
              disabled={busy}
              onChange={event => setCcText(event.target.value)}
              placeholder="e.g. family@example.com, cousin@example.com"
              className="w-full rounded-input border border-border bg-surface2 px-2.5 py-2 text-sm text-text placeholder:text-textFaint focus-visible:border-mint focus-visible:outline-hidden"
            />
            {tooManyCc ? (
              <p className="mt-1.5 text-[11px] text-danger">
                Up to {MAX_CC_EMAILS} extra recipients.
              </p>
            ) : invalid.length > 0 ? (
              <p className="mt-1.5 break-words text-[11px] text-danger">
                Doesn&apos;t look like an email: {invalid.join(', ')}
              </p>
            ) : (
              <p className="mt-1.5 text-[11.5px] text-textMuted">
                They&apos;ll be cc&apos;d on the 4-week, 2-week, 2-day, and checkout reminders.
              </p>
            )}
          </div>
        ) : null}

        {create.isError ? (
          <InlineAlert variant="danger" testId="block-create-error">
            {describeBlockError(create.error, 'create')}
          </InlineAlert>
        ) : null}
      </div>
    </SheetFrame>
  )
}

// -- owner block details -----------------------------------------------------

type Lookup<T> =
  | { status: 'pending' }
  | { status: 'error'; onRetry: () => void }
  | { status: 'missing' }
  | { status: 'ok'; record: T }

/** What a block's id resolves to in the blocks read. */
export function lookupBlock<T>(
  query: BlocksQuery,
  pick: (data: NonNullable<BlocksQuery['data']>) => T | undefined
): Lookup<T> {
  if (query.data) {
    const record = pick(query.data)
    return record ? { status: 'ok', record } : { status: 'missing' }
  }
  if (query.isError) return { status: 'error', onRetry: () => void query.refetch() }
  return { status: 'pending' }
}

function LookupProblem({
  lookup,
  what,
}: {
  lookup: Exclude<Lookup<unknown>, { status: 'ok' }>
  what: string
}) {
  if (lookup.status === 'pending') {
    return (
      <div className="space-y-3" aria-busy="true" data-testid="block-sheet-loading">
        <LoadingSkeleton className="h-6 w-1/2" />
        <LoadingSkeleton className="h-16 w-full" />
      </div>
    )
  }
  if (lookup.status === 'error') {
    return (
      <InlineAlert variant="danger">
        Couldn’t load this {what}.{' '}
        <button
          type="button"
          onClick={lookup.onRetry}
          className="font-semibold underline underline-offset-2 hover:no-underline"
        >
          Try again
        </button>
      </InlineAlert>
    )
  }
  return (
    <InlineAlert icon="info" variant="info">
      This {what} is no longer on the property. It may have been removed. Close this panel and the
      calendar will refresh.
    </InlineAlert>
  )
}

export interface OwnerBlockSheetProps {
  propertyId: string
  lookup: Lookup<OwnerBlockRecord>
  /** Which other blocks cover some of this one's nights (see `overlapNote`); left out when none does. */
  overlapNote?: string | null
  back?: BlockSheetBack
  onClose: () => void
}

export function OwnerBlockSheet({
  propertyId,
  lookup,
  overlapNote,
  back,
  onClose,
}: OwnerBlockSheetProps) {
  const record = lookup.status === 'ok' ? lookup.record : null
  const release = useReleaseOwnerBlock(propertyId)
  return (
    <SheetFrame
      testId="owner-block-sheet"
      title={record?.start && record.end ? rangeTitle(record.start, record.end) : 'Owner block'}
      description="Blocked dates"
      pill={
        record ? (
          <Pill variant={statusOf(record).variant}>{statusOf(record).label}</Pill>
        ) : undefined
      }
      back={back}
      onClose={onClose}
      locked={release.isPending}
      footer={
        record ? (
          <OwnerBlockActions release={release} record={record} onClose={onClose} />
        ) : (
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      {record ? (
        <OwnerBlockDetails record={record} overlapNote={overlapNote} />
      ) : lookup.status === 'ok' ? null : (
        <LookupProblem lookup={lookup} what="block" />
      )}
    </SheetFrame>
  )
}

function OverlapNote({ note }: { note?: string | null }) {
  if (!note) return null
  return (
    <InlineAlert icon="info" variant="info" testId="block-overlap-note">
      {note}
    </InlineAlert>
  )
}

function OwnerBlockDetails({
  record,
  overlapNote,
}: {
  record: OwnerBlockRecord
  overlapNote?: string | null
}) {
  const failedClean = record.sync_status === 'failed' && !record.ambiguous
  const failedUnsure = record.sync_status === 'failed' && record.ambiguous
  return (
    <div className="space-y-4">
      <OverlapNote note={overlapNote} />
      <div className="grid grid-cols-2 gap-2.5">
        <DetailField label="Start">
          {record.start ? `${longDate(record.start)} at ${BLOCK_CHECKIN_TIME_LABEL}` : 'Unknown'}
        </DetailField>
        <DetailField label="End">
          {record.end ? `${longDate(record.end)} at ${BLOCK_CHECKOUT_TIME_LABEL}` : 'Unknown'}
        </DetailField>
      </div>
      {record.note ? <DetailField label="Reason">{record.note}</DetailField> : null}
      <DetailField label="Cleaning needed">{record.cleaning_needed ? 'Yes' : 'No'}</DetailField>
      <DetailField label="Created by">{record.created_by_name || 'Not recorded'}</DetailField>

      {record.sync_status === 'pending' ? (
        <InlineAlert>Waiting for Uplisting to confirm this block.</InlineAlert>
      ) : null}
      {record.sync_status === 'deleting' ? (
        <InlineAlert>Removing this block. Waiting for Uplisting to confirm.</InlineAlert>
      ) : null}
      {record.has_conflict ? (
        <InlineAlert>
          A booking landed on these dates. The block is still in place, but this needs a look.
        </InlineAlert>
      ) : null}
      {record.sync_status === 'synced' && record.last_error ? (
        <InlineAlert>
          Removing this block failed last time: {record.last_error} Try again below.
        </InlineAlert>
      ) : null}
      {failedClean ? (
        <InlineAlert variant="danger">
          Sync failed{record.last_error ? `: ${record.last_error}` : ''}. These dates may still be
          bookable.
        </InlineAlert>
      ) : null}
      {failedUnsure ? (
        <InlineAlert variant="danger">
          We could not confirm this reached Uplisting. Check their dashboard before retrying.{' '}
          {record.last_error ?? null}
        </InlineAlert>
      ) : null}
    </div>
  )
}

function OwnerBlockActions({
  release,
  record,
  onClose,
}: {
  release: ReturnType<typeof useReleaseOwnerBlock>
  record: OwnerBlockRecord
  onClose: () => void
}) {
  const [confirming, setConfirming] = useState(false)
  const removing = record.sync_status === 'deleting'

  const remove = () => {
    release.mutate(record.block_id, {
      onSuccess: response => {
        toast({
          title: response.block ? 'Removing the block' : 'Block removed',
          description: response.block
            ? 'Waiting for Uplisting to confirm. Only nights no other block or booking covers will reopen.'
            : 'It had not reached Uplisting, so nothing needed freeing there.',
        })
        onClose()
      },
      onError: () => setConfirming(false),
    })
  }

  return (
    <>
      {release.isError ? (
        <div className="w-full">
          <InlineAlert variant="danger" testId="block-remove-error">
            {describeBlockError(release.error, 'remove')}
          </InlineAlert>
        </div>
      ) : null}
      {confirming ? (
        <div className="w-full">
          <InlineAlert variant="danger" testId="block-remove-confirm">
            <div className="font-semibold text-text">Remove this block?</div>
            Only nights that no other block or booking covers will reopen for booking. Nights still
            covered by another block or a reservation stay closed.
          </InlineAlert>
        </div>
      ) : null}
      {confirming ? (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirming(false)}
            disabled={release.isPending}
          >
            Keep block
          </Button>
          <Button variant="destructive" size="sm" onClick={remove} disabled={release.isPending}>
            {release.isPending ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : null}
            {release.isPending ? 'Removing…' : 'Remove block'}
          </Button>
        </>
      ) : (
        <>
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setConfirming(true)}
            disabled={removing}
          >
            Remove
          </Button>
        </>
      )}
    </>
  )
}

// -- Uplisting-app hold -------------------------------------------------------

export interface HoldSheetProps {
  propertyId: string
  lookup: Lookup<ExternalBlockRecord>
  overlapNote?: string | null
  back?: BlockSheetBack
  onClose: () => void
}

export function HoldSheet({ propertyId, lookup, overlapNote, back, onClose }: HoldSheetProps) {
  const record = lookup.status === 'ok' ? lookup.record : null
  const range = record?.start && record.end ? { start: record.start, end: record.end } : null
  return (
    <SheetFrame
      testId="hold-sheet"
      title={range ? rangeTitle(range.start, range.end) : 'Blocked in Uplisting'}
      description="Blocked dates"
      pill={<Pill variant="slate">Blocked in Uplisting</Pill>}
      back={back}
      onClose={onClose}
      footer={
        <Button variant="outline" size="sm" onClick={onClose}>
          Close
        </Button>
      }
    >
      {record && range ? (
        <div className="space-y-4">
          <OverlapNote note={overlapNote} />
          <div className="grid grid-cols-2 gap-2.5">
            <DetailField label="Start">{longDate(range.start)}</DetailField>
            <DetailField label="End">{longDate(range.end)}</DetailField>
          </div>
          <DetailField label="Created by">Uplisting app</DetailField>
          <InlineAlert>
            This hold was made in the Uplisting app. It can’t be changed here. To free these dates,
            delete it in the Uplisting app.
          </InlineAlert>
          <HoldCheck propertyId={propertyId} start={range.start} end={range.end} />
        </div>
      ) : lookup.status === 'ok' ? null : (
        <LookupProblem lookup={lookup} what="hold" />
      )}
    </SheetFrame>
  )
}

function HoldCheck({ propertyId, start, end }: { propertyId: string; start: string; end: string }) {
  const check = useCheckHold(propertyId)
  const span = `${shortDate(start)} to ${shortDate(end)}`
  const nights = check.data?.nights ?? []
  const closed = nights.filter(night => night.available === false).length
  const unknown = nights.filter(night => night.available === null).length

  let result: ReactNode = null
  if (check.isPending) {
    result = (
      <div className="flex items-start gap-2 text-[12.5px] text-textMuted" role="status">
        <Loader2 className="mt-0.5 h-3.5 w-3.5 animate-spin" aria-hidden />
        Asking Uplisting…
      </div>
    )
  } else if (check.isError) {
    result = <InlineAlert variant="danger">{describeBlockError(check.error, 'check')}</InlineAlert>
  } else if (check.data) {
    const total = nights.length
    result =
      total === 0 ? (
        <p
          className="text-[12.5px] leading-relaxed text-textMuted"
          role="status"
          data-testid="hold-check-result"
        >
          Uplisting did not report any nights for {span}, so it can’t be said whether the hold is
          still there.
        </p>
      ) : (
        <p
          className="text-[12.5px] leading-relaxed text-textMuted"
          role="status"
          data-testid="hold-check-result"
        >
          {closed > 0
            ? closed === total
              ? `Uplisting says ${span} is unavailable (${total} night${total === 1 ? '' : 's'}). The hold is still in place.`
              : `Uplisting says ${closed} of ${total} nights in ${span} are still unavailable.`
            : unknown > 0
              ? `Uplisting did not report ${unknown} of ${total} nights in ${span}, so it can’t be said whether they are open.`
              : `Uplisting says ${span} is open. The hold was deleted there. Close this panel and the calendar will update on the next refresh.`}
        </p>
      )
  }

  return (
    <div className="space-y-2.5">
      <Button
        variant="outline"
        size="sm"
        onClick={() => check.mutate({ start, end })}
        disabled={check.isPending}
      >
        Check availability
      </Button>
      {result}
    </div>
  )
}
